import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from './Button';
import Icon from '../AppIcon';

const Header = ({ isAuthenticated = false, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, signOut, isAuthenticated: authIsAuthenticated } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  // Use AuthContext authentication state first, fall back to prop
  const isUserAuthenticated = authIsAuthenticated || isAuthenticated;

  // Add logo click handler
  const handleLogoClick = () => {
    if (isUserAuthenticated) {
      navigate('/user-account-dashboard');
    } else {
      navigate('/login');
    }
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      const { error } = await signOut();
      if (!error) {
        navigate('/login');
      } else {
        console.error('Sign out error:', error);
      }
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setIsSigningOut(false);
    }
  };

  const handleProfileClick = () => {
    navigate('/user-profile-management');
    setIsMenuOpen(false);
  };

  const handleDashboardClick = () => {
    navigate('/user-account-dashboard');
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/10 backdrop-blur-xl border-b border-white/20 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo - Now Clickable */}
          <button
            onClick={handleLogoClick}
            className="flex items-center space-x-3 focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-lg transition-all hover:scale-105"
            aria-label={isUserAuthenticated ? "Go to Dashboard" : "Go to Login"}
          >
            <img
              src="/assets/images/stillflow_primary_white-1767144960179.svg"
              alt="StillFlow Logo"
              className="h-[60px] drop-shadow-lg"
            />
          </button>

          {/* Desktop Navigation */}
          {isUserAuthenticated && (
            <div className="hidden md:flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDashboardClick}
                iconName="LayoutDashboard"
                iconPosition="left"
              >
                Dashboard
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/showcase')}
                iconName="Image"
                iconPosition="left"
              >
                Showcase
              </Button>

              {/* User Profile Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={16} className="text-primary" />
                  </div>
                  <span className="font-body text-sm text-foreground max-w-32 truncate">
                    {userProfile?.full_name || user?.email?.split('@')?.[0] || 'User'}
                  </span>
                  <Icon name="ChevronDown" size={16} className="text-muted-foreground" />
                </button>

                {isMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-card border border-border rounded-lg shadow-lg py-2 animate-fade-in">
                    <div className="px-4 py-2 border-b border-border">
                      <p className="font-body text-sm font-medium text-foreground truncate">
                        {userProfile?.full_name || 'User'}
                      </p>
                      <p className="font-caption text-xs text-muted-foreground truncate">
                        {user?.email}
                      </p>
                    </div>

                    <button
                      onClick={handleProfileClick}
                      className="w-full px-4 py-2 text-left font-body text-sm text-foreground hover:bg-muted transition-colors flex items-center space-x-2"
                    >
                      <Icon name="User" size={16} />
                      <span>Profile Settings</span>
                    </button>

                    <button
                      onClick={handleSignOut}
                      disabled={isSigningOut}
                      className="w-full px-4 py-2 text-left font-body text-sm text-error hover:bg-error/10 transition-colors flex items-center space-x-2 disabled:opacity-50"
                    >
                      <Icon name={isSigningOut ? "Loader2" : "LogOut"} size={16} className={isSigningOut ? "animate-spin" : ""} />
                      <span>{isSigningOut ? 'Signing Out...' : 'Sign Out'}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {!isUserAuthenticated && (
            <div className="hidden md:flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/login')}
              >
                Sign In
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/register')}
                iconName="UserPlus"
                iconPosition="left"
              >
                Sign Up
              </Button>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <Icon name={isMenuOpen ? "X" : "Menu"} size={24} className="text-foreground" />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-border py-4 space-y-2 animate-fade-in">
            {isUserAuthenticated ? (
              <>
                <div className="px-4 py-2 border-b border-border">
                  <p className="font-body text-sm font-medium text-foreground truncate">
                    {userProfile?.full_name || 'User'}
                  </p>
                  <p className="font-caption text-xs text-muted-foreground truncate">
                    {user?.email}
                  </p>
                </div>

                <button
                  onClick={handleDashboardClick}
                  className="w-full px-4 py-2 text-left font-body text-sm text-foreground hover:bg-muted transition-colors flex items-center space-x-2"
                >
                  <Icon name="LayoutDashboard" size={16} />
                  <span>Dashboard</span>
                </button>

                <button
                  onClick={handleProfileClick}
                  className="w-full px-4 py-2 text-left font-body text-sm text-foreground hover:bg-muted transition-colors flex items-center space-x-2"
                >
                  <Icon name="User" size={16} />
                  <span>Profile Settings</span>
                </button>

                <button
                  onClick={handleSignOut}
                  disabled={isSigningOut}
                  className="w-full px-4 py-2 text-left font-body text-sm text-error hover:bg-error/10 transition-colors flex items-center space-x-2 disabled:opacity-50"
                >
                  <Icon name={isSigningOut ? "Loader2" : "LogOut"} size={16} className={isSigningOut ? "animate-spin" : ""} />
                  <span>{isSigningOut ? 'Signing Out...' : 'Sign Out'}</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full px-4 py-2 text-left font-body text-sm text-foreground hover:bg-muted transition-colors"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="w-full px-4 py-2 text-left font-body text-sm text-primary hover:bg-primary/10 transition-colors flex items-center space-x-2"
                >
                  <Icon name="UserPlus" size={16} />
                  <span>Sign Up</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;